//@flow
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import AssignmentIcon from '@material-ui/icons/Assignment';
import SchoolIcon from '@material-ui/icons/School';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import DashboardIcon from '@material-ui/icons/Dashboard';
import AssessmentIcon from '@material-ui/icons/Assessment';
import PeopleIcon from '@material-ui/icons/People';
import DescriptionIcon from '@material-ui/icons/Description';
import BusinessIcon from '@material-ui/icons/Business';
import StoreIcon from '@material-ui/icons/Store';
import SyncAltIcon from '@material-ui/icons/SyncAlt';
import BugReportIcon from '@material-ui/icons/BugReport';
import { Link, useLocation } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import { useLeftNav } from './LeftNavContext';

const collapsedDrawerWidth = 64; 
const expandedDrawerWidth = 240; 

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: collapsedDrawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerPaper: {
    width: collapsedDrawerWidth,
    backgroundColor: '#ffffff',
    borderRight: '1px solid rgba(0,0,0,0.04)',
    boxShadow: '4px 0 24px rgba(0,0,0,0.02)',
    overflowX: 'hidden',
    zIndex: 1099,
    position: 'relative', // Ensure it sits within the flex container
    height: '100%', // Take full height of parent
    transition: theme.transitions.create(['width', 'box-shadow'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '&:hover': {
      width: expandedDrawerWidth,
      boxShadow: '4px 0 32px rgba(0,0,0,0.06)',
      '& $listItemText': {
        opacity: 1,
        visibility: 'visible',
        transform: 'translateX(0)',
      },
      '& $expandIcon': {
        opacity: 1,
        visibility: 'visible',
      }
    },
  },
  toolbar: {
    minHeight: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  list: {
    padding: theme.spacing(2, 1),
  },
  listItem: {
    margin: '4px 0',
    borderRadius: '0 24px 24px 0',
    width: 'calc(100% - 8px)',
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: 20,
    position: 'relative',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f8fafc',
      '& $listItemIcon': {
        color: '#D4AF37',
      }
    },
    '&.active': {
      backgroundColor: '#fffcf2', // Very light gold tint
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 6,
        bottom: 6,
        width: 4,
        backgroundColor: '#D4AF37',
        borderRadius: '0 4px 4px 0',
      },
      '& $listItemIcon': {
        color: '#D4AF37',
      },
      '& $listItemText span': {
        fontWeight: 600,
        color: '#1e293b',
      },
    },
  },
  listItemIcon: {
    color: '#94a3b8',
    minWidth: 40,
    transition: 'color 0.2s',
  },
  listItemText: {
    opacity: 0,
    visibility: 'hidden',
    margin: 0,
    transform: 'translateX(-10px)',
    transition: theme.transitions.create(['opacity', 'visibility', 'transform'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    '& span': {
      fontSize: '0.9rem',
      fontWeight: 500,
      color: '#64748b',
      whiteSpace: 'nowrap',
    },
  },
  expandIcon: {
    opacity: 0,
    visibility: 'hidden',
    color: '#94a3b8',
    transition: theme.transitions.create(['opacity', 'visibility'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  nested: {
    paddingLeft: theme.spacing(4),
    '&.active::before': {
      left: theme.spacing(4),
    }
  },
  nestedList: {
    backgroundColor: '#fafafa',
  },
  divider: {
    margin: '16px 16px',
    backgroundColor: '#f1f5f9',
  }
}));

function LeftNav(): React$Node {
  const classes = useStyles();
  const location = useLocation();
  const [reportsOpen, setReportsOpen] = React.useState(false);
  const [workersReportsOpen, setWorkersReportsOpen] = React.useState(false);
  const { isExpanded, setIsExpanded } = useLeftNav();

  React.useEffect(() => {
    // Auto-open Reports menu when on a reports page
    if (location.pathname.includes('/filedrop') ||
        location.pathname.includes('/taskReports') ||
        location.pathname.includes('/reports/')) {
      setReportsOpen(true);
      
      // Auto-open Workers Reports submenu when on a workers report page
      if (location.pathname.includes('/reports/workers') || 
          location.pathname.includes('/reports/byFormType') ||
          location.pathname.includes('/reports/WorkersXMLsCounts')) {
        setWorkersReportsOpen(true);
      }
    }
  }, [location.pathname]);

  const handleReportsClick = () => {
    setReportsOpen(!reportsOpen);
  };

  const handleWorkersReportsClick = () => {
    setWorkersReportsOpen(!workersReportsOpen);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === path;
    }
    return location.pathname.includes(path);
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{
        paper: classes.drawerPaper,
      }}
      anchor="left"
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className={classes.toolbar} />
      <List className={classes.list}>
        <Tooltip title="PRODUCTION" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
          <ListItem 
            button 
            component={Link} 
            to="/" 
            className={`${classes.listItem} ${isActive('/') && !isActive('/qualification') && !isActive('/training') ? 'active' : ''}`}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Production" className={classes.listItemText} />
          </ListItem>
        </Tooltip>
        
        <Tooltip title="QUALIFICATION" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
          <ListItem 
            button 
            component={Link} 
            to="/qualification" 
            className={`${classes.listItem} ${isActive('/qualification') ? 'active' : ''}`}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <VerifiedUserIcon />
            </ListItemIcon>
            <ListItemText primary="Qualification" className={classes.listItemText} />
          </ListItem>
        </Tooltip>
        
        <Tooltip title="TRAINING" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
          <ListItem 
            button 
            component={Link} 
            to="/training" 
            className={`${classes.listItem} ${isActive('/training') ? 'active' : ''}`}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <SchoolIcon />
            </ListItemIcon>
            <ListItemText primary="Training" className={classes.listItemText} />
          </ListItem>
        </Tooltip>

        <Divider className={classes.divider} />

        <Tooltip title="RT VENDORS" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
          <ListItem
            button
            component={Link}
            to="/vendors"
            className={`${classes.listItem} ${isActive('/vendors') ? 'active' : ''}`}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <StoreIcon />
            </ListItemIcon>
            <ListItemText primary="RT Vendors" className={classes.listItemText} />
          </ListItem>
        </Tooltip>

        <Tooltip title="ERROR LOG" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
          <ListItem
            button
            component={Link}
            to="/errorlog"
            className={`${classes.listItem} ${isActive('/errorlog') ? 'active' : ''}`}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <BugReportIcon />
            </ListItemIcon>
            <ListItemText primary="Error Log" className={classes.listItemText} />
          </ListItem>
        </Tooltip>

        <Divider className={classes.divider} />
        
        <Tooltip title="REPORTS" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
          <ListItem 
            button 
            onClick={handleReportsClick}
            className={`${classes.listItem} ${
              (isActive('/filedrop') || isActive('/taskReports') || isActive('/reports/')) ? 'active' : ''
            }`}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <AssignmentIcon />
            </ListItemIcon>
            <ListItemText primary="Reports" className={classes.listItemText} />
            <div className={classes.expandIcon}>
              {reportsOpen ? <ExpandLess /> : <ExpandMore />}
            </div>
          </ListItem>
        </Tooltip>
        
        <Collapse in={reportsOpen} timeout="auto" unmountOnExit className={classes.nestedList}>
          <List component="div" disablePadding>
            <Tooltip title="Filedrop Reports" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
              <ListItem 
                button 
                className={`${classes.nested} ${classes.listItem} ${isActive('/filedrop') ? 'active' : ''}`}
                component={Link} 
                to="/filedrop/tier1CountsReport"
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <AssessmentIcon />
                </ListItemIcon>
                <ListItemText primary="Filedrop Reports" className={classes.listItemText} />
              </ListItem>
            </Tooltip>
            
            <Tooltip title="SFTP Transfer Stats" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
              <ListItem
                button
                className={`${classes.nested} ${classes.listItem} ${isActive('/reports/ftTransferStats') ? 'active' : ''}`}
                component={Link}
                to="/reports/ftTransferStats"
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <SyncAltIcon />
                </ListItemIcon>
                <ListItemText primary="SFTP Transfer Stats" className={classes.listItemText} />
              </ListItem>
            </Tooltip>

            <Tooltip title="Task Reports" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
              <ListItem
                button
                className={`${classes.nested} ${classes.listItem} ${isActive('/taskReports') ? 'active' : ''}`}
                component={Link}
                to="/taskReports"
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <AssessmentIcon />
                </ListItemIcon>
                <ListItemText primary="Task Reports" className={classes.listItemText} />
              </ListItem>
            </Tooltip>
            
            {/* Workers Reports Group */}
            <Tooltip title="Workers Reports" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
              <ListItem 
                button 
                onClick={handleWorkersReportsClick}
                className={`${classes.nested} ${classes.listItem} ${
                  (isActive('/reports/workers') || isActive('/reports/byFormType') || isActive('/reports/WorkersXMLsCounts')) ? 'active' : ''
                }`}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary="Workers Reports" className={classes.listItemText} />
                <div className={classes.expandIcon}>
                  {workersReportsOpen ? <ExpandLess /> : <ExpandMore />}
                </div>
              </ListItem>
            </Tooltip>
            
            <Collapse in={workersReportsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <Tooltip title="Tasks Counts" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
                  <ListItem 
                    button 
                    className={`${classes.listItem} ${isActive('/reports/workersTasksCounts') ? 'active' : ''}`}
                    style={{ paddingLeft: 56 }}
                    component={Link} 
                    to="/reports/workersTasksCounts"
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <AssessmentIcon />
                    </ListItemIcon>
                    <ListItemText primary="Tasks Counts" className={classes.listItemText} />
                  </ListItem>
                </Tooltip>
                
                <Tooltip title="By Form Type" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
                  <ListItem 
                    button 
                    className={`${classes.listItem} ${isActive('/reports/byFormType') ? 'active' : ''}`}
                    style={{ paddingLeft: 56 }}
                    component={Link} 
                    to="/reports/byFormType/workersTasksCounts"
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText primary="By Form Type" className={classes.listItemText} />
                  </ListItem>
                </Tooltip>
                
                <Tooltip title="XMLs Counts" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
                  <ListItem 
                    button 
                    className={`${classes.listItem} ${isActive('/reports/WorkersXMLsCounts') ? 'active' : ''}`}
                    style={{ paddingLeft: 56 }}
                    component={Link} 
                    to="/reports/WorkersXMLsCounts"
                  >
                    <ListItemIcon className={classes.listItemIcon}>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText primary="XMLs Counts" className={classes.listItemText} />
                  </ListItem>
                </Tooltip>
              </List>
            </Collapse>
            
            {/* Management Reports */}
            <Tooltip title="Management Counts" placement="right" disableHoverListener={isExpanded} PopperProps={{ disablePortal: true }}>
              <ListItem 
                button 
                className={`${classes.nested} ${classes.listItem} ${isActive('/reports/mgmtCounts') ? 'active' : ''}`}
                component={Link} 
                to="/reports/mgmtCounts"
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText primary="Management Counts" className={classes.listItemText} />
              </ListItem>
            </Tooltip>
          </List>
        </Collapse>
      </List>
    </Drawer>
  );
}

export default (React.memo<{||}>(LeftNav): React$AbstractComponent<{||}, mixed>);
